/*
 * Payments: the best of the old Steward's page and Jobber's. Top cards (Collected this month, Invoice payment time,
 * Invoices paid on time, Awaiting payment), then every payment with Status, Method and Date filters, search, the totals of
 * what is shown, and each row opening the payment's own page (client, summary, details, timeline).
 */
import { Link, useNavigate } from "@tanstack/react-router";
import {
  addDays,
  PAYMENT_METHOD_WORDS,
  PAYMENT_METHODS,
  PAYMENT_STATUS_WORDS,
  PAYMENT_STATUSES,
  type PaymentListRow,
  type PaymentMethod,
  type PaymentStatus,
} from "@steward/shared";
import { useState, type MouseEvent, type ReactNode } from "react";
import { useInvoice, usePayment, usePayments } from "../api/money";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { money, moneyExact, phoneText, plural, shortDate, todayIn } from "../components/format";
import { Icon } from "../components/Icon";
import { Menu } from "../components/overlays";
import { PhoneRecord } from "../components/PhoneRecord";
import { usePhone } from "../components/usePhone";
import { Button, Card, EmptyState, IconButton, LoadError, Page, Skeleton, StatusLabel, type Status } from "../components/parts";
import { ReceiptBox, RefundBox } from "./Invoices";
import { SettingsButton } from "./settings/Feature";

const STATUS_LOOK: Record<PaymentStatus, Status> = {
  succeeded: "success",
  pending: "informative",
  failed: "critical",
  refunded: "inactive",
};
function PaymentTag({ p }: { p: Pick<PaymentListRow, "status" | "refund"> }) {
  if (p.refund) return <StatusLabel status="inactive">Refund</StatusLabel>;
  const s = (PAYMENT_STATUSES as readonly string[]).includes(p.status) ? (p.status as PaymentStatus) : "succeeded";
  return <StatusLabel status={STATUS_LOOK[s]}>{PAYMENT_STATUS_WORDS[s]}</StatusLabel>;
}

const DATES = [
  ["all", "All"],
  ["7", "Past 7 days"],
  ["30", "Past 30 days"],
  ["month", "This month"],
  ["lastmonth", "Last month"],
  ["year", "This year"],
] as const;
type DateRange = (typeof DATES)[number][0];
function datesOf(range: DateRange, today: string): { from?: string; to?: string } {
  const first = `${today.slice(0, 8)}01`;
  if (range === "7" || range === "30") return { from: addDays(today, -Number(range)) };
  if (range === "month") return { from: first };
  if (range === "lastmonth") {
    const last = addDays(first, -1);
    return { from: `${last.slice(0, 8)}01`, to: last };
  }
  if (range === "year") return { from: `${today.slice(0, 4)}-01-01` };
  return {};
}

/** A filter as a chip with its value ("Method | All"), opening a short list (Jobber's filter chips). */
function PickChip<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (v: T) => void;
}) {
  const shown = options.find(([k]) => k === value)?.[1] ?? "All";
  return (
    <Menu
      align="left"
      button={({ toggle, open }) => (
        <button
          type="button"
          className="chip pick"
          aria-pressed={value !== options[0]?.[0]}
          aria-expanded={open}
          onClick={toggle}
        >
          <span className="muted">{label}</span>
          <span className="sep" aria-hidden />
          <b>{shown}</b>
          <Icon name="chevronDown" size={16} />
        </button>
      )}
    >
      {(close) =>
        options.map(([k, words]) => (
          <button
            key={k}
            type="button"
            role="menuitemradio"
            aria-checked={k === value}
            aria-current={k === value}
            onClick={() => {
              onChange(k);
              close();
            }}
          >
            {words}
          </button>
        ))
      }
    </Menu>
  );
}

/** "Same day", "1 day", "2.5 days". */
const daysWords = (d: number) => (d < 1 ? "Same day" : `${Math.round(d * 10) / 10} ${d < 1.5 ? "day" : "days"}`);

/** A tile's line comparing with the stretch before: green when better. */
function Trend({
  now,
  before,
  lowerIsBetter,
  unit,
}: {
  now: number | null;
  before: number | null;
  lowerIsBetter?: boolean;
  unit: string;
}) {
  if (now === null || before === null) return null;
  const diff = Math.round((now - before) * 10) / 10;
  if (diff === 0) return <span className="hint">Same as the stretch before</span>;
  const better = lowerIsBetter ? diff < 0 : diff > 0;
  return (
    <span className={`hint ${better ? "up" : "down"}`}>
      {diff > 0 ? "Up" : "Down"} {Math.abs(diff)} {unit} from the stretch before
    </span>
  );
}

export function PaymentsPage() {
  const { company } = useSession();
  const navigate = useNavigate();
  const today = todayIn(company.timezone);
  const [status, setStatus] = useState<PaymentStatus | "all">("all");
  const [method, setMethod] = useState<PaymentMethod | "all">("all");
  const [range, setRange] = useState<DateRange>("all");
  const [typed, setTyped] = useState("");
  const list = usePayments(company.id, {
    ...datesOf(range, today),
    ...(status === "all" ? {} : { status }),
    ...(method === "all" ? {} : { method }),
    ...(typed.trim() ? { q: typed.trim() } : {}),
  });
  const d = list.data;
  const sum = d?.summary;
  const wait = <span className="skeleton inline" />;
  const filtered = status !== "all" || method !== "all" || range !== "all" || typed.trim() !== "";
  return (
    <Page title="Payments" secondaryAction={<SettingsButton to="/invoices/settings">Payment settings</SettingsButton>}>
      <div className="tiles">
        <div className="tile">
          <span className="label">Collected this month</span>
          <span className="hint">
            {sum
              ? `${plural(sum.monthCount, "payment")}${sum.monthTipCents ? `, plus ${moneyExact(sum.monthTipCents)} in tips` : ""}`
              : ""}
          </span>
          <span className="value">{sum ? moneyExact(sum.monthCents) : wait}</span>
        </div>
        <div className="tile">
          <span className="label">Invoice payment time</span>
          <span className="hint">Past 30 days, from sent to paid</span>
          <span className="value">{sum ? (sum.payDays === null ? "None yet" : daysWords(sum.payDays)) : wait}</span>
          {sum ? <Trend now={sum.payDays} before={sum.payDaysBefore} lowerIsBetter unit="days" /> : null}
        </div>
        <div className="tile">
          <span className="label">Invoices paid on time</span>
          <span className="hint">Past 60 days, paid before past due</span>
          <span className="value">{sum ? (sum.onTimePct === null ? "None yet" : `${sum.onTimePct}%`) : wait}</span>
          {sum ? <Trend now={sum.onTimePct} before={sum.onTimePctBefore} unit="points" /> : null}
        </div>
        <Link to="/invoices" className="tile link-tile">
          <span className="label">Awaiting payment</span>
          <span className="hint">{sum ? plural(sum.owedCount, "sent invoice") : ""}</span>
          <span className="value">{sum ? money(sum.owedCents) : wait}</span>
        </Link>
      </div>
      <DataTable<PaymentListRow>
        title="All payments"
        rows={d?.payments ?? []}
        rowKey={(p) => p.id}
        loading={list.isFetching}
        error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
        query={{ value: typed, onChange: setTyped, placeholder: "Search payments" }}
        onRowClick={(p) => void navigate({ to: "/payments/$id", params: { id: p.id } })}
        rowClass={(p) => (p.status === "failed" || p.refund ? "muted-row" : undefined)}
        tools={
          <>
            <div className="chips">
              <PickChip
                label="Status"
                value={status}
                onChange={setStatus}
                options={[["all", "All"], ...PAYMENT_STATUSES.map((s) => [s, PAYMENT_STATUS_WORDS[s]] as const)]}
              />
              <PickChip
                label="Method"
                value={method}
                onChange={setMethod}
                options={[["all", "All"], ...PAYMENT_METHODS.map((m) => [m, PAYMENT_METHOD_WORDS[m]] as const)]}
              />
              <PickChip label="Date" value={range} onChange={setRange} options={DATES} />
            </div>
            <p className="muted small table-sum">
              {d ? (
                <>
                  {plural(d.count, "payment")}, {moneyExact(d.totalCents)}
                  {d.tipCents ? `, plus ${moneyExact(d.tipCents)} in tips` : ""}
                  {d.count > d.payments.length ? `. Newest ${d.payments.length} shown.` : ""}
                </>
              ) : (
                " "
              )}
            </p>
          </>
        }
        empty={
          <EmptyState icon="payments" title={filtered ? "No payments match" : "No payments yet"}>
            {filtered
              ? "Try another status, method, date or search."
              : "Card payments from the pay page and cash or checks you record show here."}
          </EmptyState>
        }
        columns={[
          {
            key: "client",
            header: "Client",
            sort: (p) => p.clientName.toLowerCase(),
            render: (p) => (
              <span className="two-line">
                <b>{p.clientName}</b>
                {p.invoiceId && p.invoiceNumber ? (
                  <Link
                    to="/invoices/$id"
                    params={{ id: p.invoiceId }}
                    className="small"
                    onClick={(e: MouseEvent) => e.stopPropagation()}
                  >
                    Invoice #{p.invoiceNumber}
                  </Link>
                ) : (
                  <span className="muted small">{p.invoiceId ? "Invoice" : "No invoice"}</span>
                )}
              </span>
            ),
          },
          {
            key: "date",
            header: "Payment date",
            sort: (p) => p.receivedAt,
            render: (p) => <span className="nowrap">{shortDate(dayIn(company.timezone, p.receivedAt))}</span>,
          },
          { key: "status", header: "Status", sort: (p) => p.status, render: (p) => <PaymentTag p={p} /> },
          {
            key: "method",
            header: "Method",
            sort: (p) => p.method,
            render: (p) => (
              <span className="method">
                {p.method === "card" ? <Icon name="payments" size={16} /> : null}
                {PAYMENT_METHOD_WORDS[p.method]}
                {p.checkNumber ? ` #${p.checkNumber}` : ""}
              </span>
            ),
          },
          { key: "through", header: "Paid through", render: (p) => <span className="muted">{p.paidThrough}</span> },
          {
            key: "amount",
            header: "Amount",
            align: "right",
            sort: (p) => p.amountCents,
            render: (p) => (
              <span className="two-line right">
                <b className="num">{moneyExact(p.amountCents - p.tipCents)}</b>
                {p.tipCents ? <span className="tip num">+ {moneyExact(p.tipCents)} tip</span> : null}
              </span>
            ),
          },
        ]}
      />
    </Page>
  );
}

/** The day a moment falls on in the company's time zone. */
const dayIn = (tz: string, iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(iso));
/** "Oct 29, 2026, 6:28 PM" in the company's time zone. */
const momentIn = (tz: string, iso: string) =>
  new Intl.DateTimeFormat("en-US", { timeZone: tz, dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));

export function PaymentPage({ id }: { id: string }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const pay = usePayment(id);
  const p = pay.data;
  const invoice = useInvoice(p?.invoiceId ?? "");
  const [open, setOpen] = useState<"receipt" | "refund" | null>(null);
  const phone = usePhone();
  const back = (
    <Link to="/payments" className="back-link">
      <Icon name="chevronLeft" size={16} /> All payments
    </Link>
  );
  if (pay.error)
    return (
      <Page title="Payment">
        {back}
        <LoadError error={pay.error} retry={() => void pay.refetch()} />
      </Page>
    );
  if (!p)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const inv = p.invoiceId ? invoice.data : undefined;
  const canRefund = can("card.charge") && !p.refund && p.amountCents > 0 && p.status === "succeeded" && inv !== undefined;
  const canReceipt = can("invoice.send") && !p.refund && p.status === "succeeded" && inv !== undefined;
  const rows: [string, ReactNode][] = [
    ["Transaction date", momentIn(company.timezone, p.receivedAt)],
    ["Method", `${PAYMENT_METHOD_WORDS[p.method]}${p.checkNumber ? ` #${p.checkNumber}` : ""}`],
    ["Paid through", p.paidThrough],
    [
      "Applied to",
      p.invoiceId ? (
        <Link to="/invoices/$id" params={{ id: p.invoiceId }}>
          {p.invoiceNumber ? `Invoice #${p.invoiceNumber}` : "Invoice"}
        </Link>
      ) : (
        "No invoice"
      ),
    ],
  ];
  if (p.visitDate) rows.push(["For the visit on", shortDate(p.visitDate)]);
  if (p.transaction) rows.push(["Transaction number", <span className="mono small">{p.transaction}</span>]);
  if (p.note) rows.push(["Note", p.note]);
  if (p.refundOf)
    rows.push([
      "Gives back",
      <Link to="/payments/$id" params={{ id: p.refundOf }}>
        The first payment
      </Link>,
    ]);
  const primary = canReceipt ? (
    <Button icon="mail" onClick={() => setOpen("receipt")}>
      Send receipt
    </Button>
  ) : null;
  const secondary = p.invoiceId ? (
    <Button
      type="secondary"
      icon="invoices"
      onClick={() => void navigate({ to: "/invoices/$id", params: { id: p.invoiceId ?? "" } })}
    >
      Invoice
    </Button>
  ) : null;
  const moreMenu = canRefund ? (
    <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
      {(close) => (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            close();
            setOpen("refund");
          }}
        >
          Refund
        </button>
      )}
    </Menu>
  ) : null;
  const summary = (
    <Card title="Payment summary" flush>
      <table className="data sum-table">
        <tbody>
          <tr>
            <td>Payment amount</td>
            <td className="right num">{moneyExact(p.amountCents - p.tipCents)}</td>
          </tr>
          {p.tipCents ? (
            <tr>
              <td>Tip</td>
              <td className="right num">{moneyExact(p.tipCents)}</td>
            </tr>
          ) : null}
          <tr className="total">
            <td>
              {p.refund ? "Given back" : p.status === "succeeded" ? "Paid" : PAYMENT_STATUS_WORDS[p.status as PaymentStatus]}
            </td>
            <td className="right num">{moneyExact(p.amountCents)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
  const details = (
    <Card title="Details">
      <dl className="details grid">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
  const timeline = (
    <Card title="Timeline">
      <ol className="timeline">
        {p.timeline.map((t, n) => (
          <li key={n}>
            <b>
              {t.what}
              {t.amountCents ? ` ${moneyExact(t.amountCents)}` : ""}
            </b>
            <span className="muted small">{momentIn(company.timezone, t.at)}</span>
          </li>
        ))}
      </ol>
    </Card>
  );
  const sheets = (
    <>
      {open === "receipt" && inv ? <ReceiptBox i={inv} onClose={() => setOpen(null)} /> : null}
      {open === "refund" && inv ? <RefundBox i={inv} paymentId={p.id} onClose={() => setOpen(null)} /> : null}
    </>
  );

  if (phone)
    return (
      <PhoneRecord
        back={{ label: "All payments", go: () => void navigate({ to: "/payments" }) }}
        status={
          <>
            <PaymentTag p={p} />
            <span className="muted">{PAYMENT_METHOD_WORDS[p.method]}</span>
          </>
        }
        title={moneyExact(p.amountCents)}
        sub={
          <button
            type="button"
            className="link"
            onClick={() => void navigate({ to: "/clients/$id", params: { id: p.client.id } })}
          >
            {p.client.name} <Icon name="chevronRight" size={16} />
          </button>
        }
        phone={p.client.phone}
        email={p.client.email}
        facts={[
          { key: "when", icon: "clock" as const, main: momentIn(company.timezone, p.receivedAt), sub: p.paidThrough },
          ...(p.invoiceId
            ? [
                {
                  key: "invoice",
                  icon: "invoices" as const,
                  main: p.invoiceNumber ? `Invoice #${p.invoiceNumber}` : "Invoice",
                  sub: p.visitDate ? `For the visit on ${shortDate(p.visitDate)}` : "Open the invoice",
                  onClick: () => void navigate({ to: "/invoices/$id", params: { id: p.invoiceId ?? "" } }),
                },
              ]
            : []),
          ...(p.client.address
            ? [
                {
                  key: "where",
                  icon: "pin" as const,
                  main: p.client.address,
                  sub: "Directions",
                  directions: p.client.address,
                },
              ]
            : []),
        ]}
        actions={
          <>
            {primary}
            {moreMenu}
          </>
        }
      >
        {summary}
        {details}
        {timeline}
        {sheets}
      </PhoneRecord>
    );

  return (
    <Page
      title={<span className="num">{moneyExact(p.amountCents)}</span>}
      titleMetaData={<PaymentTag p={p} />}
      subtitle={back}
      primaryAction={primary}
      secondaryAction={secondary}
      moreActions={moreMenu}
    >
      <div className="split payment-page">
        <div className="stack">
          <Card>
            <div className="pay-client">
              <Link to="/clients/$id" params={{ id: p.client.id }} className="h3">
                {p.client.name}
              </Link>
              {p.client.address ? (
                <span>
                  <span className="muted small">Property address</span>
                  <br />
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(p.client.address)}`} target="_blank" rel="noreferrer">
                    {p.client.address}
                  </a>
                </span>
              ) : null}
              <span className="pay-contact">
                {p.client.phone ? <a href={`tel:${p.client.phone}`}>{phoneText(p.client.phone)}</a> : null}
                {p.client.email ? <a href={`mailto:${p.client.email}`}>{p.client.email}</a> : null}
              </span>
            </div>
          </Card>
          {summary}
          {details}
        </div>
        <div className="stack">{timeline}</div>
      </div>
      {sheets}
    </Page>
  );
}
